import React, { useEffect } from "react";
import "./PrivacyPolicy.css";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

const PrivacyPolicy = () => {
  useScrollAnimation();

  useEffect(() => {
    // Add animation classes to elements on load
    const animateElements = document.querySelectorAll(".animate-on-load");
    animateElements.forEach((element, index) => {
      element.style.animationDelay = `${0.2 + index * 0.1}s`;
    });
  }, []);

  const lastUpdated = "January 1, 2024";

  return (
    <>
      <Header />
      <div className="privacy-policy-page">
        {/* Hero Section */}
        <section className="privacy-hero">
          <div className="hero-background">
            <div className="overlay-dark"></div>
          </div>
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title animate-on-load">
                Privacy <span className="text-accent">Policy</span>
              </h1>
              <p className="hero-subtitle animate-on-load">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </section>

        {/* Privacy Policy Content */}
        <section className="privacy-content">
          <div className="container">
            <div className="privacy-container">
              {/* Introduction */}
              <div className="privacy-section animate-on-scroll">
                <h2>Introduction</h2>
                <p>
                  At Avvoka Exports ("we," "our," or "us"), we are committed to
                  protecting your privacy and ensuring the security of your
                  personal information. This Privacy Policy explains how we
                  collect, use, disclose, and safeguard your information when
                  you visit our website or use our services.
                </p>
                <p>
                  By accessing our website or using our services, you consent to
                  the practices described in this Privacy Policy. We encourage
                  you to read this policy carefully to understand our views and
                  practices regarding your personal data.
                </p>
              </div>

              {/* Information We Collect */}
              <div className="privacy-section animate-on-scroll delay-1">
                <h2>Information We Collect</h2>

                <h3>Personal Information</h3>
                <p>
                  We may collect the following types of personal information:
                </p>
                <ul>
                  <li>
                    Contact Information (name, email address, phone number)
                  </li>
                  <li>Business Information (company name, position)</li>
                  <li>Communication preferences</li>
                  <li>Inquiry and customer service information</li>
                  <li>Billing and payment information</li>
                </ul>

                <h3>Automatically Collected Information</h3>
                <p>
                  When you visit our website, we automatically collect certain
                  information:
                </p>
                <ul>
                  <li>IP address and browser type</li>
                  <li>Device information</li>
                  <li>Usage data and browsing patterns</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>

              {/* How We Use Your Information */}
              <div className="privacy-section animate-on-scroll delay-2">
                <h2>How We Use Your Information</h2>
                <p>We use your information for the following purposes:</p>
                <ul>
                  <li>To provide and maintain our services</li>
                  <li>To process your inquiries and requests</li>
                  <li>
                    To communicate with you about our products and services
                  </li>
                  <li>To improve our website and user experience</li>
                  <li>To analyze trends and user behavior</li>
                  <li>To prevent fraud and enhance security</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </div>

              {/* Data Sharing and Disclosure */}
              <div className="privacy-section animate-on-scroll delay-3">
                <h2>Data Sharing and Disclosure</h2>
                <p>We may share your information with:</p>

                <h3>Service Providers</h3>
                <p>
                  Third-party vendors who provide services on our behalf, such
                  as payment processing, data analysis, email delivery, and
                  hosting services.
                </p>

                <h3>Business Partners</h3>
                <p>
                  Trusted partners who assist us in providing our services,
                  always under strict confidentiality agreements.
                </p>

                <h3>Legal Requirements</h3>
                <p>
                  When required by law, regulation, legal process, or
                  governmental request.
                </p>

                <h3>Business Transfers</h3>
                <p>
                  In connection with a merger, acquisition, or sale of all or a
                  portion of our assets.
                </p>
              </div>

              {/* Data Security */}
              <div className="privacy-section animate-on-scroll">
                <h2>Data Security</h2>
                <p>
                  We implement appropriate technical and organizational measures
                  to protect your personal information against unauthorized
                  access, alteration, disclosure, or destruction. These measures
                  include:
                </p>
                <ul>
                  <li>SSL encryption for data transmission</li>
                  <li>Secure server infrastructure</li>
                  <li>Regular security assessments</li>
                  <li>Access controls and authentication procedures</li>
                  <li>Employee training on data protection</li>
                </ul>
                <p>
                  While we strive to use commercially acceptable means to
                  protect your personal information, no method of transmission
                  over the Internet or electronic storage is 100% secure.
                </p>
              </div>

              {/* Cookies and Tracking Technologies */}
              <div className="privacy-section animate-on-scroll delay-1">
                <h2>Cookies and Tracking Technologies</h2>
                <p>
                  We use cookies and similar tracking technologies to track
                  activity on our website and store certain information. Types
                  of cookies we use:
                </p>

                <h3>Essential Cookies</h3>
                <p>Required for the operation of our website.</p>

                <h3>Analytical/Performance Cookies</h3>
                <p>Allow us to recognize and count the number of visitors.</p>

                <h3>Functionality Cookies</h3>
                <p>
                  Enable us to personalize content and remember your
                  preferences.
                </p>

                <h3>Targeting Cookies</h3>
                <p>Record your visit to our website and the pages you visit.</p>

                <p>
                  You can control cookies through your browser settings.
                  However, disabling cookies may affect your experience on our
                  website.
                </p>
              </div>

              {/* Your Rights */}
              <div className="privacy-section animate-on-scroll delay-2">
                <h2>Your Rights</h2>
                <p>
                  Depending on your location, you may have the following rights:
                </p>
                <ul>
                  <li>Access to your personal information</li>
                  <li>Correction of inaccurate or incomplete data</li>
                  <li>Erasure of your personal information</li>
                  <li>Restriction of processing your data</li>
                  <li>Data portability</li>
                  <li>Objection to processing</li>
                  <li>Withdrawal of consent</li>
                </ul>
                <p>
                  To exercise any of these rights, please contact us using the
                  information provided in the "Contact Us" section.
                </p>
              </div>

              {/* Data Retention */}
              <div className="privacy-section animate-on-scroll delay-3">
                <h2>Data Retention</h2>
                <p>
                  We will retain your personal information only for as long as
                  necessary to fulfill the purposes for which we collected it,
                  including to satisfy any legal, accounting, or reporting
                  requirements.
                </p>
                <p>
                  To determine the appropriate retention period, we consider the
                  amount, nature, and sensitivity of the personal data, the
                  potential risk of harm from unauthorized use or disclosure,
                  the purposes for which we process it, and whether we can
                  achieve those purposes through other means.
                </p>
              </div>

              {/* International Data Transfers */}
              <div className="privacy-section animate-on-scroll">
                <h2>International Data Transfers</h2>
                <p>
                  Your information may be transferred to and processed in
                  countries other than the country in which you reside. These
                  countries may have data protection laws that are different
                  from the laws of your country.
                </p>
                <p>
                  We ensure appropriate safeguards are in place for
                  international data transfers, including standard contractual
                  clauses and other approved mechanisms.
                </p>
              </div>

              {/* Children's Privacy */}
              <div className="privacy-section animate-on-scroll delay-1">
                <h2>Children's Privacy</h2>
                <p>
                  Our website is not intended for children under the age of 18.
                  We do not knowingly collect personal information from
                  children. If you are a parent or guardian and believe your
                  child has provided us with personal information, please
                  contact us, and we will delete such information from our
                  systems.
                </p>
              </div>

              {/* Changes to This Policy */}
              <div className="privacy-section animate-on-scroll delay-2">
                <h2>Changes to This Policy</h2>
                <p>
                  We may update our Privacy Policy from time to time. We will
                  notify you of any changes by posting the new Privacy Policy on
                  this page and updating the "Last updated" date.
                </p>
                <p>
                  We encourage you to review this Privacy Policy periodically
                  for any changes. Changes to this Privacy Policy are effective
                  when they are posted on this page.
                </p>
              </div>

              {/* Contact Us */}
              <div className="privacy-section animate-on-scroll delay-3">
                <h2>Contact Us</h2>
                <p>
                  If you have any questions or concerns about this Privacy
                  Policy or our data practices, please contact us at:
                </p>
                <div className="contact-info">
                  <p>
                    <strong>Avvoka Exports</strong>
                  </p>
                  <p>Email: privacy@avvokaexports.com</p>
                  <p>Phone: +254 700 123 456</p>
                  <p>Address: Nairobi, Kenya</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="privacy-actions">
          <div className="container">
            <div className="actions-grid">
              <div className="action-card animate-on-scroll">
                <h3>Download Policy</h3>
                <p>Download a printable version of our Privacy Policy</p>
                <button className="btn btn-secondary">
                  Download PDF
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7 10L12 15L17 10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 15V3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <div className="action-card animate-on-scroll delay-1">
                <h3>Update Preferences</h3>
                <p>
                  Manage your communication preferences and consent settings
                </p>
                <button className="btn btn-primary">Manage Preferences</button>
              </div>
              <div className="action-card animate-on-scroll delay-2">
                <h3>Data Request</h3>
                <p>Submit a request regarding your personal data</p>
                <button className="btn btn-secondary">Submit Request</button>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
